import React, { useState } from "react";
import { AlertTriangle, ShieldAlert, Waves, User, Anchor, Library, Globe, Eye, RefreshCw, Sparkles, Scale, X, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { orionImageUrl, challengeCitation, triangulateCitation, groundCitation, getCitationLineage } from "../../lib/api";
import { TranslateButton } from "../TranslateButton";

// Detect "pick one" option blocks in an assistant response.
//
// The Sentinel frequently replies with 2+ bold-headed drafts like:
//
//   **The "Grounded & Grateful" One:**
//   "GM. Taking a second to appreciate…"
//
//   **The "Short & Sweet" One:**
//   "Good morning, world…"
//
//   Which one feels most like you today?
//
// Instead of making the Operator type "Grounded & Grateful" back, we
// render each label as a tap-to-reply chip. Detection rule:
//   • find every `**<label>:**` heading in the message
//   • strip meta words (The / One / parenthetical / quotes)
//   • blocklist generic markdown headers so we don't offer
//     "Summary"/"Example" as choices
//   • only render if 2+ distinct survive
const OPTION_HEADING_RE = /\*\*([^*\n]{2,80}?)\s*:\s*\*\*/g;
const OPTION_BLOCKLIST = new Set([
  "summary", "example", "examples", "note", "notes", "tip", "tips",
  "warning", "important", "steps", "step", "step 1", "step 2",
  "overview", "conclusion", "tl;dr", "answer", "question", "context",
  "caveat", "pros", "cons", "pro", "con", "reasoning", "rationale",
]);

function extractCitations(text) {
  // Scholar's Citation Tether renders footnotes like:
  //   [^1]: ✓ *cited from your archive: foo.pdf* · `citation:abc12345`
  //   [^2]: ⚠ *uncited — drawn from training data...* · `citation:def67890`
  // We parse those into structured objects so each becomes a tap-to-
  // challenge badge. The 8-char prefix in the rendered text matches the
  // first 8 chars of the full citation_id returned by the backend, so
  // we ask the backend to resolve the full id when the user challenges.
  if (!text || typeof text !== "string") return [];
  const out = [];
  const re = /\[\^(\d+)\]:\s*([✓◆⚠])\s*\*([^*]+)\*[^`]*`citation:([a-f0-9-]{6,36})`/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    const [, index, glyph, description, idPrefix] = m;
    out.push({
      index: Number(index),
      glyph,
      description: description.trim(),
      idPrefix,
      status: glyph === "✓" ? "archive" : glyph === "◆" ? "memory" : "uncited",
    });
  }
  return out;
}

// Strip the trailing `\n\n---\n**Sources:**\n[^N]: ...` block emitted by
// backend/citations.py:287. Citations render as interactive badges via
// CitationChallengeBar; the raw markdown footer would only duplicate them
// as plain text. Inline `[^N]` references inside the prose are preserved.
function stripSourcesFooter(text) {
  if (!text || typeof text !== "string") return text;
  // Match the trailing footer regardless of how many leading newlines
  // separate it from the prose: `\n+---\n**Sources:**\n...EOF`.
  return text.replace(/\n+---\s*\n\*\*Sources:\*\*[\s\S]*$/m, "").trimEnd();
}

function CitationChallengeBar({ citations, sessionId }) {
  const [open, setOpen] = useState(null); // active citation badge
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [triangulating, setTriangulating] = useState(false);
  const [grounding, setGrounding] = useState(false);
  const [lineage, setLineage] = useState(null);
  const [statuses, setStatuses] = useState({}); // idPrefix → epistemic_status

  if (!citations || citations.length === 0) return null;

  const openCitation = async (c) => {
    setOpen(c);
    setReason("");
    setLineage(null);
    try {
      const l = await getCitationLineage(c.idPrefix);
      setLineage(l);
      if (l?.epistemic_status) {
        setStatuses((prev) => ({ ...prev, [c.idPrefix]: l.epistemic_status }));
      }
    } catch {
      /* lineage fetch is best-effort */
    }
  };

  const submit = async () => {
    if (!open) return;
    if (reason.trim().length < 5) {
      toast.error("Tell the Sentinel why you think this is off — at least a few words.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await challengeCitation({ idPrefix: open.idPrefix, reason: reason.trim() });
      toast.success("Challenge recorded", {
        description: "Signed into the citation's lineage. Tap Triangulate to verify automatically.",
      });
      setReason("");
      if (res?.citation?.epistemic_status) {
        setStatuses((prev) => ({ ...prev, [open.idPrefix]: res.citation.epistemic_status }));
      }
      // Refresh lineage to show the new challenge
      try {
        const l = await getCitationLineage(open.idPrefix);
        setLineage(l);
      } catch { /* */ }
    } catch (e) {
      toast.error("Could not record challenge", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const triangulateNow = async () => {
    if (!open) return;
    setTriangulating(true);
    try {
      const res = await triangulateCitation(open.idPrefix);
      const v = res?.verdict || {};
      toast.success(`Triangulation: ${v.verdict || "done"}`, {
        description:
          (v.reasoning || "Verdict signed onto citation lineage.").slice(0, 200),
      });
      if (res?.epistemic_status) {
        setStatuses((prev) => ({ ...prev, [open.idPrefix]: res.epistemic_status }));
      }
      try {
        const l = await getCitationLineage(open.idPrefix);
        setLineage(l);
      } catch { /* */ }
    } catch (e) {
      toast.error("Triangulation failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setTriangulating(false);
    }
  };

  const groundNow = async () => {
    if (!open) return;
    setGrounding(true);
    try {
      const res = await groundCitation(open.idPrefix);
      const sourceCount = (res?.grounding?.sources || []).length;
      toast.success(`🌐 Web-grounded · ${sourceCount} source${sourceCount === 1 ? "" : "s"}`, {
        description: "Real source URLs signed onto the citation lineage.",
      });
      if (res?.epistemic_status) {
        setStatuses((prev) => ({ ...prev, [open.idPrefix]: res.epistemic_status }));
      }
      try {
        const l = await getCitationLineage(open.idPrefix);
        setLineage(l);
      } catch { /* */ }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      if (String(detail).includes("GEMINI_API_KEY")) {
        toast.error("Grounding unavailable", {
          description: "Architect must set GEMINI_API_KEY for web grounding.",
        });
      } else {
        toast.error("Grounding failed", { description: detail });
      }
    } finally {
      setGrounding(false);
    }
  };

  // Map epistemic_status → visual badge style + glyph
  const badgeTone = (status, fallback) => {
    if (!status) return fallback;
    if (status === "fact") return "border-emerald-500 text-emerald-300 bg-emerald-500/10";
    if (status === "web_cited") return "border-sky-500 text-sky-300 bg-sky-500/10";
    if (status === "refuted") return "border-rose-500 text-rose-300 bg-rose-500/10";
    if (status === "contested") return "border-amber-500 text-amber-300 bg-amber-500/10";
    if (status === "theory") return "border-violet-500 text-violet-300 bg-violet-500/10";
    if (status === "challenged") return "border-amber-500/60 text-amber-400";
    if (status === "inconclusive") return "border-zinc-600 text-zinc-400";
    return fallback;
  };
  const badgeGlyph = (status, fallback) => {
    if (status === "fact") return "✓";
    if (status === "web_cited") return "🌐";
    if (status === "refuted") return "✗";
    if (status === "contested") return "⚖";
    if (status === "theory") return "◆";
    if (status === "challenged") return "⚠";
    if (status === "inconclusive") return "?";
    return fallback;
  };

  return (
    <>
      <div
        className="mt-3 flex flex-wrap gap-1.5 pt-3 border-t border-zinc-800"
        data-testid="citation-challenge-bar"
      >
        <span className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 flex items-center mr-1">
          <Scale className="h-2.5 w-2.5 mr-1" /> Verify a claim:
        </span>
        {citations.map((c) => {
          const status = statuses[c.idPrefix];
          const fallbackTone =
            c.status === "archive"
              ? "border-emerald-500/40 text-emerald-400 hover:border-emerald-500"
              : c.status === "memory"
              ? "border-sky-500/40 text-sky-400 hover:border-sky-500"
              : "border-zinc-700 text-zinc-400 hover:border-amber-500 hover:text-amber-400";
          const tone = badgeTone(status, fallbackTone);
          const glyph = badgeGlyph(status, c.glyph);
          return (
            <button
              key={c.idPrefix}
              type="button"
              onClick={() => openCitation(c)}
              data-testid={`citation-badge-${c.idPrefix}`}
              title={status ? `${status} · ${c.description}` : c.description}
              className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] uppercase tracking-[0.2em] transition-colors ${tone}`}
            >
              {glyph} [^{c.index}]{status ? ` · ${status}` : ""}
            </button>
          );
        })}
      </div>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4 overflow-y-auto"
          data-testid="citation-challenge-modal"
          role="dialog"
        >
          <div className="w-full max-w-xl border border-amber-500/40 bg-[#0b0b0d] p-6 my-8 shadow-[0_0_60px_rgba(245,158,11,0.15)]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-amber-300">
                <Scale className="h-3.5 w-3.5" /> △ L10 · Verify Citation [^{open.index}]
              </div>
              <button
                type="button"
                onClick={() => setOpen(null)}
                data-testid="citation-challenge-cancel"
                className="text-zinc-500 hover:text-zinc-300"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <h2 className="font-heading text-xl mt-3 text-zinc-100">
              What looks off?
            </h2>
            <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
              The Sentinel cited this claim as:
              <br />
              <span className="text-emerald-300 font-mono text-[10px]">
                {open.description}
              </span>
            </p>

            {lineage && lineage.triangulation_count > 0 && (
              <div className="mt-4 border border-zinc-800 bg-zinc-950 p-3">
                <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                  Triangulation history · current status: <span className="text-emerald-400">{lineage.epistemic_status}</span>
                </div>
                {(lineage.triangulations || []).slice(-2).map((t, i) => (
                  <div key={i} className="text-[11px] text-zinc-300 mb-2">
                    <span className="text-emerald-400 font-mono">{t.verdict}</span>
                    <span className="text-zinc-500"> · {t.confidence}% · agreed: {(t.agreeing_sources || []).join(", ") || "—"} · disagreed: {(t.disagreeing_sources || []).join(", ") || "—"}</span>
                    <div className="text-zinc-500 mt-0.5">{t.reasoning}</div>
                  </div>
                ))}
              </div>
            )}

            {lineage && lineage.grounding && (lineage.grounding.sources || []).length > 0 && (
              <div className="mt-4 border border-sky-500/30 bg-sky-500/5 p-3" data-testid="citation-grounding-block">
                <div className="text-[9px] uppercase tracking-[0.3em] text-sky-300 mb-2 flex items-center gap-1.5">
                  <Globe className="h-3 w-3" />
                  🌐 Web-grounded · {(lineage.grounding.sources || []).length} source{(lineage.grounding.sources || []).length === 1 ? "" : "s"} · {lineage.grounding.model || "gemini"}
                </div>
                {lineage.grounding.answer && (
                  <p className="text-[11px] text-zinc-200 leading-relaxed mb-2 italic">
                    {lineage.grounding.answer.slice(0, 320)}{lineage.grounding.answer.length > 320 ? "…" : ""}
                  </p>
                )}
                <ul className="space-y-1.5">
                  {(lineage.grounding.sources || []).slice(0, 8).map((s, i) => (
                    <li key={i} className="text-[11px]">
                      <a
                        href={s.uri}
                        target="_blank"
                        rel="noreferrer noopener"
                        data-testid={`grounding-source-${i}`}
                        className="inline-flex items-center gap-1 text-sky-300 hover:text-sky-200 hover:underline"
                      >
                        <ExternalLink className="h-2.5 w-2.5" />
                        {s.title || (s.uri || "").replace(/^https?:\/\//, "").split("/")[0]}
                      </a>
                      {s.segment && (
                        <div className="text-zinc-500 text-[10px] italic ml-4 mt-0.5">&ldquo;{s.segment.slice(0, 160)}{s.segment.length > 160 ? "…" : ""}&rdquo;</div>
                      )}
                    </li>
                  ))}
                </ul>
                {lineage.grounding.signature_sha256 && (
                  <div className="mt-2 pt-2 border-t border-sky-500/20 text-[9px] uppercase tracking-[0.25em] text-sky-400/70 font-mono">
                    HMAC: {String(lineage.grounding.signature_sha256).slice(0, 28)}…
                  </div>
                )}
              </div>
            )}

            <p className="text-xs text-zinc-500 mt-3 leading-relaxed">
              Tell us what is wrong and why. Your reasoning is HMAC-signed and chained into the citation&apos;s lineage — it cannot be deleted silently.
            </p>
            <textarea
              autoFocus
              data-testid="citation-challenge-textarea"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. 'The actual figure was 90 ft, not 100 ft. Source: USGS 2024 mineralogy survey.'"
              className="mt-3 w-full bg-zinc-950 border border-zinc-800 focus:border-amber-400 focus:outline-none text-xs p-3 font-body text-zinc-100 placeholder:text-zinc-600 min-h-[100px]"
            />
            <div className="mt-5 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end sm:flex-wrap">
              <button
                type="button"
                onClick={() => setOpen(null)}
                disabled={submitting || triangulating || grounding}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-zinc-700 text-zinc-300 hover:border-zinc-500 disabled:opacity-40 text-xs uppercase tracking-[0.3em]"
              >
                Close
              </button>
              <button
                type="button"
                onClick={groundNow}
                disabled={submitting || triangulating || grounding}
                data-testid="citation-ground-btn"
                title="Ask Gemini to fetch real source URLs for this claim via Google Search (costs 1 Gemini call)"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-sky-500 text-sky-300 hover:bg-sky-500 hover:text-zinc-950 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-sky-300 text-xs uppercase tracking-[0.3em]"
              >
                {grounding ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Grounding…
                  </>
                ) : (
                  <>
                    <Globe className="h-3.5 w-3.5" />
                    Cite via Google
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={triangulateNow}
                disabled={submitting || triangulating || grounding}
                data-testid="citation-triangulate-btn"
                title="Cross-check this claim against Claude + GPT + your archive + memory (costs ~3 LLM calls)"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-emerald-500 text-emerald-300 hover:bg-emerald-500 hover:text-zinc-950 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-emerald-300 text-xs uppercase tracking-[0.3em]"
              >
                {triangulating ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Triangulating…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-3.5 w-3.5" />
                    Triangulate
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={submitting || triangulating || grounding || reason.trim().length < 5}
                data-testid="citation-challenge-submit"
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 border border-amber-400 text-amber-300 hover:bg-amber-400 hover:text-zinc-950 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-amber-300 disabled:cursor-not-allowed text-xs uppercase tracking-[0.3em]"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Signing…
                  </>
                ) : (
                  <>
                    <Scale className="h-3.5 w-3.5" />
                    Record challenge
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function extractOptions(text) {
  if (!text || typeof text !== "string") return [];
  const out = [];
  const seen = new Set();
  let m;
  const re = new RegExp(OPTION_HEADING_RE.source, "g");
  while ((m = re.exec(text)) !== null) {
    let label = m[1]
      .trim()
      .replace(/^The\s+/i, "")
      .replace(/\s+One$/i, "")
      .replace(/[“”‘’"']/g, "")
      .replace(/\s*\([^)]*\)\s*$/, "")
      .trim();
    if (label.length < 2 || label.length > 60) continue;
    const key = label.toLowerCase();
    if (OPTION_BLOCKLIST.has(key)) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(label);
  }
  return out.length >= 2 ? out : [];
}

export default function MessageBubble({ message, onRephrase, onRemix, onOptionClick }) {
  const isUser = message.role === "user";

  if (isUser) {
    return (
      <div
        className="flex justify-end"
        data-testid={`user-message-${message.id || "pending"}`}
      >
        <div className="max-w-[82%] border border-zinc-800 bg-zinc-950 p-4">
          <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            <User className="h-3 w-3" /> Operator
          </div>
          {message.attachments && message.attachments.length > 0 && (
            <div
              className="mb-3 flex flex-wrap gap-2"
              data-testid={`user-message-attachments-${message.id || "pending"}`}
            >
              {message.attachments.map((a) => (
                <a
                  key={a.id}
                  href={a.preview_url}
                  target="_blank"
                  rel="noreferrer"
                  className="block border border-zinc-800 hover:border-emerald-500/60 transition-colors"
                  title={a.name}
                >
                  <img
                    src={a.preview_url}
                    alt={a.name || "attachment"}
                    className="h-24 w-24 object-cover"
                  />
                </a>
              ))}
            </div>
          )}
          <div className="text-sm text-zinc-100 whitespace-pre-wrap leading-relaxed">
            {message.text}
          </div>
          <TranslateButton
            text={message.text}
            inline
            testIdPrefix={`translate-user-msg-${message.id || "pending"}`}
            className="mt-2"
          />
        </div>
      </div>
    );
  }

  // Assistant variants
  const variants = {
    anchor: {
      border: "border-amber-500/60",
      glow: "glow-amber",
      accent: "text-amber-400",
      icon: ShieldAlert,
      label: "Taurian Anchor · blocked",
    },
    drift: {
      border: "border-red-500/50",
      glow: "glow-drift",
      accent: "text-red-400",
      icon: Waves,
      label: "Obsidian Sink · neutralized",
    },
    truth: {
      border: "border-amber-500/50",
      glow: "glow-amber",
      accent: "text-amber-400",
      icon: AlertTriangle,
      label: "Truth Tether · verification requested",
    },
    clean: {
      border: "border-emerald-500/40",
      glow: "",
      accent: "text-emerald-400",
      icon: Anchor,
      label: "Sentinel · 528 Hz",
    },
    orion: {
      border: "border-emerald-500/60",
      glow: "",
      accent: "text-emerald-400",
      icon: Eye,
      label: "Orion Vision · 528 Hz",
    },
  };

  const variant = message.image_id
    ? variants.orion
    : message.anchor_blocked
    ? variants.anchor
    : message.truth_drift
    ? variants.truth
    : message.drift_detected
    ? variants.drift
    : variants.clean;
  const Icon = variant.icon;

  return (
    <div
      className="flex justify-start"
      data-testid={`sentinel-message-${message.id || "pending"}`}
    >
      <div
        className={`max-w-[82%] border ${variant.border} ${variant.glow} bg-[#0b0b0d] p-4`}
      >
        <div
          className={`flex items-center gap-2 mb-2 text-[10px] uppercase tracking-[0.3em] ${variant.accent}`}
        >
          <Icon className="h-3 w-3" /> {variant.label}
          {message.pulse_triggered && (
            <span
              className="ml-2 inline-flex items-center gap-1 border border-emerald-500/50 text-emerald-400 px-2 py-0.5 text-[9px] tracking-[0.3em]"
              data-testid="pulse-badge"
            >
              <AlertTriangle className="h-2.5 w-2.5" /> Pulse · {message.pulse_count ?? ""}
            </span>
          )}
        </div>
        <div className="text-sm text-zinc-100 whitespace-pre-wrap leading-relaxed">
          {stripSourcesFooter(message.text)}
          {message.is_ack && (
            <span
              className="inline-flex items-center gap-1 ml-2 align-middle"
              data-testid="ack-typing-dots"
              aria-label="Rendering"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-sentinel-dot" style={{ animationDelay: "0ms" }} />
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-sentinel-dot" style={{ animationDelay: "160ms" }} />
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-sentinel-dot" style={{ animationDelay: "320ms" }} />
            </span>
          )}
        </div>
        {(() => {
          if (!onOptionClick) return null;
          const options = extractOptions(message.text);
          if (options.length === 0) return null;
          return (
            <div
              className="mt-3 flex flex-wrap gap-2"
              data-testid={`message-options-${message.id || "pending"}`}
            >
              {options.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => onOptionClick(opt)}
                  data-testid={`message-option-${opt
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-+|-+$/g, "")
                    .slice(0, 40)}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500 hover:text-zinc-950 transition-colors"
                >
                  <Sparkles className="h-3 w-3" />
                  {opt}
                </button>
              ))}
            </div>
          );
        })()}
        {message.image_id && (
          <>
            <a
              href={orionImageUrl(message.image_id)}
              target="_blank"
              rel="noreferrer"
              className="mt-3 block border border-zinc-800 hover:border-emerald-500/60 transition-colors"
              data-testid={`sentinel-image-${message.image_id}`}
            >
              <img
                src={orionImageUrl(message.image_id)}
                alt={message.text || "Orion vision"}
                loading="lazy"
                className="w-full max-h-[460px] object-cover"
              />
            </a>
            {message.vision_prompt && onRemix && (
              <button
                type="button"
                onClick={() => onRemix(message.vision_prompt)}
                data-testid={`sentinel-image-remix-${message.image_id}`}
                className="mt-2 w-full flex items-center justify-center gap-2 py-2 text-[10px] uppercase tracking-[0.3em] border border-zinc-800 text-zinc-400 hover:border-emerald-500 hover:text-emerald-400 transition-colors"
              >
                <RefreshCw className="h-3 w-3" />
                Remix
              </button>
            )}
          </>
        )}
        {message.drift_detected && message.drift_flags?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {message.drift_flags.map((f) => (
              <span
                key={f}
                className="border border-red-500/40 text-red-400 px-2 py-0.5 text-[9px] uppercase tracking-[0.2em]"
              >
                {f.replace(/_/g, " ")}
              </span>
            ))}
          </div>
        )}

        {(message.archive_hits > 0 || message.web_hits > 0) && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="grounding-badges">
            {message.archive_hits > 0 && (
              <span className="inline-flex items-center gap-1 border border-emerald-500/40 text-emerald-400 px-2 py-0.5 text-[9px] uppercase tracking-[0.2em]">
                <Library className="h-2.5 w-2.5" />
                Archive · {message.archive_hits}
              </span>
            )}
            {message.web_hits > 0 && (
              <span className="inline-flex items-center gap-1 border border-sky-500/40 text-sky-400 px-2 py-0.5 text-[9px] uppercase tracking-[0.2em]">
                <Globe className="h-2.5 w-2.5" />
                Web · {message.web_hits}
              </span>
            )}
          </div>
        )}
        <CitationChallengeBar
          citations={extractCitations(message.text)}
          sessionId={message.session_id}
        />
        {message.tokens !== undefined && message.tokens > 0 && (
          <div
            className="mt-3 text-[10px] uppercase tracking-[0.3em] text-zinc-600"
            title="Tokens consumed by this exchange — feeds the 2,500-token Self-Correction Pulse counter"
          >
            {message.tokens} tokens · pulse fuel
          </div>
        )}
      </div>
    </div>
  );
}
